/**
 * Referral attachment uploads, direct from the device to storage.
 *
 * The old path POSTed each file to `/api/files/upload`, where the API buffered the whole body and
 * then re-sent it to R2 — every byte travelled twice, one file at a time, and a large video could
 * outlive the hosting platform's request timeout. Now the API only validates the file's name, type
 * and size and hands back a presigned R2 PUT (`/api/files/referral-attachment/upload-url`); the
 * bytes go straight to storage.
 *
 * If the direct path cannot be used — the API predates the endpoint, R2 refuses the browser's
 * origin (bucket CORS), or the PUT fails for any other reason — the file falls back to the proxied
 * `/api/files/upload`, so the worst case is the old behaviour rather than a failed upload. A real
 * rejection from the validator (too large, wrong type, signed out) is NOT retried: the proxied
 * endpoint would refuse the same file for the same reason.
 */

import { api, ApiError } from './api';

export const REFERRAL_ATTACHMENTS_BUCKET = 'referral-attachments';

/** How many files upload at once. Enough to overlap round trips without starving one big video. */
export const REFERRAL_UPLOAD_CONCURRENCY = 3;

/** Same fields `/api/files/upload` returns, whichever path the bytes took. */
export interface UploadedReferralAttachment {
  path: string;
  url?: string;
  fileName: string;
  fileSize: number;
  contentType: string;
}

export interface UploadProgress {
  loaded: number;
  total: number;
}

export interface UploadReferralAttachmentOptions {
  /** Byte progress of the direct upload. Not reported when the proxied fallback is used. */
  onProgress?: (progress: UploadProgress) => void;
  /**
   * Cancels the upload wherever it is (upload URL, PUT, or fallback). The promise then rejects
   * with an UploadAbortedError and the fallback is never tried.
   */
  signal?: AbortSignal;
}

interface UploadUrlResponse {
  data: {
    uploadUrl: string;
    contentType: string;
    url?: string;
    path: string;
  };
}

/** Validator answers that the proxied endpoint would give too — retrying them there is pointless. */
const FINAL_PRESIGN_STATUSES = new Set([400, 401, 403, 413, 415]);

export class DirectUploadError extends Error {
  constructor(public status: number, message: string) {
    super(message);
    this.name = 'DirectUploadError';
  }
}

/** The caller cancelled the upload (its AbortSignal fired). Not a failure to show the user. */
export class UploadAbortedError extends Error {
  constructor() {
    super('Upload cancelled');
    this.name = 'UploadAbortedError';
  }
}

/** The picked file could not be read back from the device (see stabilizeAttachmentFile). */
export class AttachmentReadError extends Error {
  constructor(fileName: string) {
    super(`Could not read "${fileName}" from this device. Please pick it again.`);
    this.name = 'AttachmentReadError';
  }
}

/**
 * Files up to this size are copied into memory before anything reads them. Bigger ones (long
 * videos) stay as the picker handed them over, since holding them in memory risks the WebView.
 */
export const STABILIZE_MAX_BYTES = 64 * 1024 * 1024;

const stabilizedFiles = new WeakSet<Blob>();

/**
 * Copies a picked file into memory, once, so every later read is of the same bytes.
 *
 * A File from `<input type="file">` is only a handle. On Android the photo picker hands the
 * WebView a content:// handle whose reported modification time is not stable, and Chromium
 * refuses to read a file whose modification time has changed since it was picked
 * (net::ERR_UPLOAD_FILE_CHANGED). That made the upload body, the proxied fallback, the thumbnail
 * and image compression all fail with "Failed to fetch" on Android, seconds after a perfectly
 * good pick. Reading the bytes right away and uploading the copy sidesteps it. Returns the same
 * File when it is already a copy or above STABILIZE_MAX_BYTES.
 */
export async function stabilizeAttachmentFile(file: File): Promise<File> {
  if (stabilizedFiles.has(file) || file.size > STABILIZE_MAX_BYTES) return file;
  let bytes: ArrayBuffer;
  try {
    bytes = await readFileBytes(file);
  } catch {
    throw new AttachmentReadError(file.name);
  }
  const copy = new File([bytes], file.name, { type: file.type, lastModified: file.lastModified });
  stabilizedFiles.add(copy);
  return copy;
}

/** FileReader rather than Blob.arrayBuffer(): available in every WebView this app runs in. */
function readFileBytes(file: Blob): Promise<ArrayBuffer> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as ArrayBuffer);
    reader.onerror = () => reject(reader.error ?? new Error('read failed'));
    reader.readAsArrayBuffer(file);
  });
}

/**
 * PUT a file to a presigned URL, reporting byte progress. `contentType` must be the exact value
 * the URL was signed with. Resolves on 2xx; rejects with a DirectUploadError otherwise (status 0
 * for network/CORS failures, which the browser does not distinguish).
 */
export function putFileWithProgress(
  url: string,
  file: Blob,
  contentType: string,
  onProgress?: (progress: UploadProgress) => void,
  signal?: AbortSignal
): Promise<void> {
  return new Promise((resolve, reject) => {
    if (signal?.aborted) return reject(new DirectUploadError(0, 'Upload aborted'));
    const xhr = new XMLHttpRequest();
    const abort = () => xhr.abort();
    signal?.addEventListener('abort', abort, { once: true });
    const settle = (fn: () => void) => {
      signal?.removeEventListener('abort', abort);
      fn();
    };
    xhr.open('PUT', url);
    xhr.setRequestHeader('Content-Type', contentType);
    if (onProgress) {
      xhr.upload.onprogress = (event) => {
        onProgress({ loaded: event.loaded, total: event.lengthComputable ? event.total : file.size });
      };
    }
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        onProgress?.({ loaded: file.size, total: file.size });
        settle(resolve);
      } else {
        settle(() => reject(new DirectUploadError(xhr.status, `Storage rejected the upload (HTTP ${xhr.status})`)));
      }
    };
    xhr.onerror = () => settle(() => reject(new DirectUploadError(0, 'Network error during upload')));
    xhr.onabort = () => settle(() => reject(new DirectUploadError(0, 'Upload aborted')));
    xhr.send(file);
  });
}

/** The pre-existing proxied upload: the API receives the bytes and stores them itself. */
async function uploadViaApi(file: File, signal?: AbortSignal): Promise<UploadedReferralAttachment> {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('bucket', REFERRAL_ATTACHMENTS_BUCKET);

  try {
    const response = await api.post('/api/files/upload', formData, { signal });
    if (!response?.data?.path) throw new Error('Upload failed');
    return response.data;
  } catch (error) {
    if (signal?.aborted) throw new UploadAbortedError();
    throw error;
  }
}

/** Upload one referral attachment, direct to storage when possible. */
export async function uploadReferralAttachmentFile(
  file: File,
  options: UploadReferralAttachmentOptions = {}
): Promise<UploadedReferralAttachment> {
  const { signal } = options;
  if (signal?.aborted) throw new UploadAbortedError();

  // Read the bytes once, up front: both the direct PUT and the proxied fallback send this copy.
  file = await stabilizeAttachmentFile(file);
  if (signal?.aborted) throw new UploadAbortedError();

  let presigned: UploadUrlResponse['data'];
  try {
    const response = await api.post<UploadUrlResponse>(
      '/api/files/referral-attachment/upload-url',
      { fileName: file.name, contentType: file.type, size: file.size },
      { signal }
    );
    presigned = response.data;
  } catch (error) {
    if (signal?.aborted) throw new UploadAbortedError();
    if (error instanceof ApiError && FINAL_PRESIGN_STATUSES.has(error.status)) throw error;
    console.warn('[Upload] Direct upload unavailable, using the API upload instead:', error);
    return uploadViaApi(file, signal);
  }

  try {
    await putFileWithProgress(presigned.uploadUrl, file, presigned.contentType, options.onProgress, signal);
  } catch (error) {
    if (signal?.aborted) {
      // The bytes may have landed just before the abort; usually there is nothing to delete.
      discardReferralAttachment(presigned.path);
      throw new UploadAbortedError();
    }
    console.warn('[Upload] Direct upload failed, retrying through the API:', error);
    return uploadViaApi(file, signal);
  }

  return {
    path: presigned.path,
    url: presigned.url,
    fileName: file.name,
    fileSize: file.size,
    contentType: presigned.contentType,
  };
}

/**
 * Delete an uploaded attachment the user removed before sending the referral. Fire-and-forget:
 * the API refuses keys a referral references, and anything missed here (offline, closed tab) is
 * picked up by the server's orphaned-attachment sweep.
 */
export function discardReferralAttachment(path: string): void {
  api.delete('/api/files/referral-attachment', { path }).catch((error) => {
    console.warn('[Upload] Could not delete a removed attachment; the cleanup sweep will:', error);
  });
}

/**
 * Run `fn` over `items` with at most `limit` in flight. Results keep the input order and never
 * reject — each is a PromiseSettledResult, like Promise.allSettled.
 */
export async function mapWithConcurrency<T, R>(
  items: readonly T[],
  limit: number,
  fn: (item: T, index: number) => Promise<R>
): Promise<PromiseSettledResult<R>[]> {
  const results: PromiseSettledResult<R>[] = new Array(items.length);
  let next = 0;

  const worker = async () => {
    while (next < items.length) {
      const index = next++;
      try {
        results[index] = { status: 'fulfilled', value: await fn(items[index], index) };
      } catch (reason) {
        results[index] = { status: 'rejected', reason };
      }
    }
  };

  await Promise.all(Array.from({ length: Math.min(Math.max(limit, 1), items.length) }, worker));
  return results;
}

/**
 * A shared "at most `limit` at once" gate for work that arrives over time — files picked in
 * several batches still share one pool of upload slots. (mapWithConcurrency needs the whole list
 * up front.) Queued work starts in the order it was handed in.
 */
export function createConcurrencyLimiter(limit: number) {
  let active = 0;
  const queue: Array<() => void> = [];

  return function run<R>(fn: () => Promise<R>): Promise<R> {
    return new Promise<R>((resolve, reject) => {
      const start = () => {
        active++;
        Promise.resolve()
          .then(fn)
          .then(resolve, reject)
          .finally(() => {
            active--;
            queue.shift()?.();
          });
      };
      if (active < Math.max(limit, 1)) start();
      else queue.push(start);
    });
  };
}
